import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, ImageUp, Trash2, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { contrastWithWhite } from '../lib/branding';
import { CopyField } from './integration-shell';
import { SignInPageSettings } from './sign-in-page-settings';
import { useToast } from './toast';
import { Alert, Button, Card, CardHeader, cn, ErrorState, Field, Input, PageLoader, Select, Switch } from './ui';

type Provisioning = 'PEOPLE' | 'DOMAIN' | 'INVITED';

export interface SignInSettingsData {
  name: string;
  slug: string;
  googleSignIn: boolean;
  googleDomains: string[];
  passwordSignIn: boolean;
  signInProvisioning: Provisioning;
  signInDefaultRole: 'REQUESTER' | 'VIEWER';
  brandColor: string | null;
  logoUrl: string | null;
  signInImageUrl: string | null;
  /** Null is Edvora's own text; an empty string hides it. */
  signInHeadline: string | null;
  signInMessage: string | null;
  defaultSignInHeadline: string;
  defaultSignInMessage: string;
  googleConfigured: boolean;
  googleAvailable: boolean;
  signInUrl: string;
  redirectUri: string;
}

const PROVISIONING: { value: Provisioning; title: string; text: string }[] = [
  { value: 'PEOPLE', title: 'People in your directory', text: 'Students and staff on the People page (for example from Blackbaud) get an account the first time they sign in.' },
  { value: 'DOMAIN', title: 'Anyone on these domains', text: 'Every Google Workspace account on your domains gets an account the first time they sign in.' },
  { value: 'INVITED', title: 'Only users you add', text: 'Add people under Users & roles first. Nobody else can sign in.' },
];

const DEFAULT_COLOR = '#0aa5c2';

function readFile(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function Branding({ settings }: { settings: SignInSettingsData }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [color, setColor] = useState(settings.brandColor ?? DEFAULT_COLOR);
  useEffect(() => setColor(settings.brandColor ?? DEFAULT_COLOR), [settings.brandColor]);

  const refresh = (data: SignInSettingsData) => {
    queryClient.setQueryData(['tenant-sign-in'], data);
    void queryClient.invalidateQueries({ queryKey: ['public-school'] });
  };
  const saveColor = useMutation({
    mutationFn: (brandColor: string | null) => api.put<SignInSettingsData>('/tenant/branding', { brandColor }),
    onSuccess: (data) => {
      refresh(data);
      toast.success('School color saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const uploadLogo = useMutation({
    mutationFn: async (file: File) => api.put<SignInSettingsData>('/tenant/logo', { image: await readFile(file) }),
    onSuccess: (data) => {
      refresh(data);
      toast.success('Logo uploaded');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const removeLogo = useMutation({
    mutationFn: () => api.delete<SignInSettingsData>('/tenant/logo'),
    onSuccess: (data) => {
      refresh(data);
      toast.success('Logo removed');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const validColor = /^#[0-9a-f]{6}$/i.test(color);
  const lowContrast = validColor && contrastWithWhite(color) < 2.5;

  return (
    <Card>
      <CardHeader title="Branding" description="Your logo and color on the sign-in page and throughout the app." />
      <div className="grid gap-8 p-5 lg:grid-cols-2">
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-900">Logo</h3>
          <div className="flex items-center gap-4">
            <div className="flex size-20 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
              {settings.logoUrl ? <img src={settings.logoUrl} alt="School logo" className="size-full object-contain p-1" /> : <ImageUp className="size-6 text-slate-400" />}
            </div>
            <div className="space-y-2">
              <label className={cn('inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50', uploadLogo.isPending && 'opacity-60')}>
                <ImageUp className="size-4" />
                {settings.logoUrl ? 'Replace logo' : 'Upload logo'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="sr-only"
                  disabled={uploadLogo.isPending}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadLogo.mutate(file);
                    e.target.value = '';
                  }}
                />
              </label>
              {settings.logoUrl && (
                <Button size="sm" variant="ghost" icon={<Trash2 />} loading={removeLogo.isPending} onClick={() => removeLogo.mutate()}>
                  Remove
                </Button>
              )}
              <p className="text-xs text-slate-500">PNG, JPEG, or WebP up to 512 KB. A square image works best.</p>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-900">School color</h3>
          <div className="flex flex-wrap items-center gap-3">
            <input type="color" value={validColor ? color : DEFAULT_COLOR} onChange={(e) => setColor(e.target.value)} className="h-10 w-14 cursor-pointer rounded border border-slate-300 bg-white p-1" aria-label="School color" />
            <Input value={color} onChange={(e) => setColor(e.target.value.trim())} className="w-32 font-mono" maxLength={7} />
            <Button variant="primary" disabled={!validColor} loading={saveColor.isPending} onClick={() => saveColor.mutate(color)}>
              Save color
            </Button>
            {settings.brandColor && (
              <Button variant="ghost" onClick={() => saveColor.mutate(null)}>
                Use Edvora’s color
              </Button>
            )}
          </div>
          <div className="flex gap-1" aria-hidden>
            {[92, 70, 40, 0].map((white) => (
              <span key={white} className="h-6 flex-1 rounded" style={{ background: validColor ? `color-mix(in oklab, ${color}, white ${white}%)` : undefined }} />
            ))}
            {[20, 45].map((black) => (
              <span key={black} className="h-6 flex-1 rounded" style={{ background: validColor ? `color-mix(in oklab, ${color}, black ${black}%)` : undefined }} />
            ))}
          </div>
          {lowContrast && (
            <Alert tone="amber" icon={<TriangleAlert />}>
              This color is very light, so white text on buttons may be hard to read. A darker shade works better.
            </Alert>
          )}
        </section>

        <div className="lg:col-span-2">
          <CopyField label="Sign-in page" value={settings.signInUrl} hint="Share this with students and staff, or link to it from your school website." />
          <a href={settings.signInUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline">
            Open the sign-in page <ExternalLink className="size-3.5" />
          </a>
        </div>
      </div>
    </Card>
  );
}

function GoogleSignIn({ settings }: { settings: SignInSettingsData }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(() => ({ ...settings, domains: settings.googleDomains.join(', ') }));
  useEffect(() => setForm({ ...settings, domains: settings.googleDomains.join(', ') }), [settings]);

  const save = useMutation({
    mutationFn: () =>
      api.put<SignInSettingsData>('/tenant/sign-in', {
        googleSignIn: form.googleSignIn,
        googleDomains: form.domains,
        passwordSignIn: form.passwordSignIn,
        signInProvisioning: form.signInProvisioning,
        signInDefaultRole: form.signInDefaultRole,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['tenant-sign-in'], data);
      void queryClient.invalidateQueries({ queryKey: ['public-school'] });
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      toast.success('Sign-in settings saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Card>
      <CardHeader title="Sign-in" description="Students and staff sign in with their school Google accounts. There are no passwords to issue, reset, or rotate." />
      <div className="max-w-2xl space-y-6 p-5">
        {!settings.googleConfigured && (
          <Alert tone="amber" icon={<TriangleAlert />} title="Google sign-in isn’t set up on this Edvora environment">
            Ask Edvora support to configure the Google OAuth client (GOOGLE_OAUTH_CLIENT_ID and GOOGLE_OAUTH_CLIENT_SECRET) with the redirect URI {settings.redirectUri}.
          </Alert>
        )}

        <Switch
          label="Sign in with Google Workspace"
          description="Show “Sign in with Google” on your school’s sign-in page."
          checked={form.googleSignIn}
          onChange={(googleSignIn) => setForm({ ...form, googleSignIn })}
        />

        <Field label="Google Workspace domains" hint="Comma separated, e.g. lincoln-academy.org, students.lincoln-academy.org. Only accounts your Workspace manages can sign in.">
          <Input value={form.domains} onChange={(e) => setForm({ ...form, domains: e.target.value })} placeholder="your-school.org" className="font-mono text-sm" />
        </Field>

        <fieldset className="space-y-2">
          <legend className="mb-2 text-sm font-medium text-slate-800">Who can sign in</legend>
          {PROVISIONING.map((option) => (
            <label
              key={option.value}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3',
                form.signInProvisioning === option.value ? 'border-brand-500 bg-brand-50/60 ring-1 ring-brand-500' : 'border-slate-200 hover:border-slate-300',
              )}
            >
              <input
                type="radio"
                name="provisioning"
                className="mt-1"
                checked={form.signInProvisioning === option.value}
                onChange={() => setForm({ ...form, signInProvisioning: option.value })}
              />
              <span>
                <span className="block text-sm font-medium text-slate-900">{option.title}</span>
                <span className="block text-xs text-slate-500">{option.text}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {form.signInProvisioning !== 'INVITED' && (
          <Field label="Role for new accounts" hint="Promote technicians and administrators under Users & roles.">
            <Select value={form.signInDefaultRole} onChange={(e) => setForm({ ...form, signInDefaultRole: e.target.value as 'REQUESTER' | 'VIEWER' })} className="w-60">
              <option value="REQUESTER">Requester - reports problems</option>
              <option value="VIEWER">Viewer - read-only access</option>
            </Select>
          </Field>
        )}

        <Switch
          label="Allow password sign-in"
          description="Turn off so everyone signs in with Google. Edvora support can still help if Google sign-in stops working."
          checked={form.passwordSignIn}
          onChange={(passwordSignIn) => setForm({ ...form, passwordSignIn })}
        />
        {!form.passwordSignIn && (
          <Alert tone="blue">People who used a password will sign in with Google next time. Their Edvora passwords stop working for this school.</Alert>
        )}

        <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
          Save sign-in settings
        </Button>
      </div>
    </Card>
  );
}

export function SignInSettings() {
  const settings = useQuery({ queryKey: ['tenant-sign-in'], queryFn: () => api.get<SignInSettingsData>('/tenant/sign-in') });
  if (settings.isLoading) return <PageLoader />;
  if (settings.error || !settings.data) return <ErrorState error={settings.error} onRetry={() => settings.refetch()} />;
  return (
    <div className="space-y-6">
      <GoogleSignIn settings={settings.data} />
      <Branding settings={settings.data} />
      <SignInPageSettings settings={settings.data} />
    </div>
  );
}
